import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { makeSafeObjectPath, uploadToBucket } from '../../utils/storage';

const MAX_IMAGES = 20;

const FeedEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get('id') || '';
  const { user, role } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [isPublished, setIsPublished] = useState(true);

  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [thumbnailSelection, setThumbnailSelection] = useState('');

  const aliveExistingImages = useMemo(
    () => (existingImages || []).filter((img) => !removedImageIds.includes(img.id)),
    [existingImages, removedImageIds]
  );

  const totalImageCount = aliveExistingImages.length + newFiles.length;

  const load = useCallback(async () => {
    if (!postId) return;

    setIsLoading(true);
    setError('');

    try {
      const { data: row, error: postErr } = await supabase
        .from('ch_feed_posts')
        .select('id, title, author_id, content_html, is_published, thumbnail_image_path')
        .eq('id', postId)
        .maybeSingle();

      if (postErr || !row) {
        setError(postErr?.message || '글을 불러오지 못했습니다.');
        return;
      }

      if (!user?.id || (row.author_id !== user.id && role !== 'admin')) {
        setError('수정 권한이 없습니다.');
        return;
      }

      const { data: imageRows } = await supabase
        .from('ch_feed_images')
        .select('id, file_path, original_name, created_at')
        .eq('post_id', row.id)
        .order('created_at', { ascending: true });

      setTitle(row.title || '');
      setContentHtml(row.content_html || '');
      setIsPublished(!!row.is_published);
      setExistingImages(
        (imageRows || []).map((img) => ({
          id: img.id,
          filePath: img.file_path,
          originalName: img.original_name || 'image',
          url: img.file_path ? supabase.storage.from('feed-images').getPublicUrl(img.file_path).data.publicUrl : '',
        }))
      );
      setRemovedImageIds([]);
      setNewFiles([]);
      setThumbnailSelection(row.thumbnail_image_path ? `path:${row.thumbnail_image_path}` : '');
    } finally {
      setIsLoading(false);
    }
  }, [postId, role, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const onSelectFiles = useCallback(
    (event) => {
      const picked = Array.from(event?.target?.files || []);
      if (picked.length === 0) return;

      const remain = MAX_IMAGES - totalImageCount;
      if (remain <= 0) {
        setError(`이미지는 최대 ${MAX_IMAGES}장까지 등록할 수 있습니다.`);
        return;
      }

      const next = picked.slice(0, remain);
      if (picked.length > remain) {
        setError(`최대 ${MAX_IMAGES}장 제한으로 ${remain}장만 추가되었습니다.`);
      } else {
        setError('');
      }
      const wrapped = next.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
      }));
      setNewFiles((prev) => [...prev, ...wrapped]);
      event.target.value = '';
    },
    [totalImageCount]
  );

  const removeNewFile = (imageId) => {
    setNewFiles((prev) => prev.filter((img) => img.id !== imageId));
    if (thumbnailSelection === `new:${imageId}`) {
      setThumbnailSelection('');
    }
  };

  const toggleExistingImage = (id) => {
    const target = existingImages.find((img) => img.id === id);
    setRemovedImageIds((prev) => {
      const willRemove = !prev.includes(id);
      if (willRemove && target?.filePath && thumbnailSelection === `path:${target.filePath}`) {
        setThumbnailSelection('');
      }
      return willRemove ? [...prev, id] : prev.filter((x) => x !== id);
    });
  };

  const validate = () => {
    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return false;
    }

    const plainText = String(contentHtml || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (!plainText) {
      setError('내용을 입력해주세요.');
      return false;
    }

    if (totalImageCount > MAX_IMAGES) {
      setError(`이미지는 최대 ${MAX_IMAGES}장까지 등록할 수 있습니다.`);
      return false;
    }

    return true;
  };

  const save = useCallback(async () => {
    if (!user?.id) {
      navigate('/login-registration');
      return;
    }

    if (!validate()) return;

    setIsSaving(true);
    setError('');

    try {
      const payload = {
        title: title.trim(),
        content_html: contentHtml,
        is_published: !!isPublished,
      };

      let savedPostId = postId;

      if (postId) {
        const { data: origin } = await supabase.from('ch_feed_posts').select('author_id').eq('id', postId).maybeSingle();
        if (!origin || (origin.author_id !== user.id && role !== 'admin')) {
          setError('수정 권한이 없습니다.');
          setIsSaving(false);
          return;
        }

        const { error: updateErr } = await supabase.from('ch_feed_posts').update(payload).eq('id', postId);
        if (updateErr) {
          setError(updateErr.message || '글 수정에 실패했습니다.');
          setIsSaving(false);
          return;
        }
      } else {
        const { data: inserted, error: insertErr } = await supabase
          .from('ch_feed_posts')
          .insert({
            author_id: user.id,
            author_name:
              user?.user_metadata?.username
              || user?.user_metadata?.full_name
              || user?.email
              || '익명',
            ...payload,
          })
          .select('id')
          .single();

        if (insertErr || !inserted?.id) {
          setError(insertErr?.message || '글 등록에 실패했습니다.');
          setIsSaving(false);
          return;
        }

        savedPostId = inserted.id;
      }

      const toDeleteRows = (existingImages || []).filter((img) => removedImageIds.includes(img.id));
      if (toDeleteRows.length > 0) {
        await supabase.from('ch_feed_images').delete().in('id', toDeleteRows.map((x) => x.id));
        const paths = toDeleteRows.map((x) => x.filePath).filter(Boolean);
        if (paths.length > 0) {
          await supabase.storage.from('feed-images').remove(paths);
        }
      }

      if (newFiles.length > 0) {
        const imagePayload = [];
        const uploadedPathByTempId = new Map();
        for (const item of newFiles) {
          const file = item.file;
          const path = makeSafeObjectPath({
            userId: user.id,
            originalName: file.name,
            prefix: 'feed-images',
          });

          const { error: uploadErr } = await uploadToBucket({
            bucket: 'feed-images',
            path,
            blob: file,
            contentType: file.type || 'application/octet-stream',
            cacheControl: '31536000',
          });

          if (uploadErr) {
            setError(uploadErr.message || '이미지 업로드에 실패했습니다.');
            setIsSaving(false);
            return;
          }

          imagePayload.push({
            post_id: savedPostId,
            file_path: path,
            original_name: file.name,
            content_type: file.type || null,
            size_bytes: file.size || null,
          });
          uploadedPathByTempId.set(item.id, path);
        }

        const { error: metaErr } = await supabase.from('ch_feed_images').insert(imagePayload);
        if (metaErr) {
          setError(metaErr.message || '이미지 메타 저장에 실패했습니다.');
          setIsSaving(false);
          return;
        }

        const selectedNewPrefix = 'new:';
        if (thumbnailSelection.startsWith(selectedNewPrefix)) {
          const tempId = thumbnailSelection.slice(selectedNewPrefix.length);
          const selectedPath = uploadedPathByTempId.get(tempId) || '';
          const { error: thumbnailErr } = await supabase
            .from('ch_feed_posts')
            .update({ thumbnail_image_path: selectedPath || null })
            .eq('id', savedPostId);
          if (thumbnailErr) {
            setError(thumbnailErr.message || '대표 썸네일 저장에 실패했습니다.');
            setIsSaving(false);
            return;
          }
        }
      }

      if (thumbnailSelection.startsWith('path:')) {
        const selectedPath = thumbnailSelection.slice(5);
        const wasDeleted = toDeleteRows.some((x) => x.filePath === selectedPath);
        const { error: thumbnailErr } = await supabase
          .from('ch_feed_posts')
          .update({ thumbnail_image_path: wasDeleted ? null : (selectedPath || null) })
          .eq('id', savedPostId);
        if (thumbnailErr) {
          setError(thumbnailErr.message || '대표 썸네일 저장에 실패했습니다.');
          setIsSaving(false);
          return;
        }
      }

      if (!thumbnailSelection) {
        const { error: thumbnailErr } = await supabase
          .from('ch_feed_posts')
          .update({ thumbnail_image_path: null })
          .eq('id', savedPostId);
        if (thumbnailErr) {
          setError(thumbnailErr.message || '대표 썸네일 저장에 실패했습니다.');
          setIsSaving(false);
          return;
        }
      }

      navigate(`/feed-detail?id=${savedPostId}`);
    } finally {
      setIsSaving(false);
    }
  }, [
    contentHtml,
    existingImages,
    isPublished,
    navigate,
    newFiles,
    postId,
    removedImageIds,
    role,
    thumbnailSelection,
    title,
    user?.email,
    user?.id,
    user?.user_metadata?.full_name,
    user?.user_metadata?.username,
  ]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title={postId ? '피드 수정' : '피드 작성'} canonicalPath="/feed-editor" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">{postId ? '피드 수정' : '피드 작성'}</h1>
              <p className="text-sm text-muted-foreground mt-1">HTML 에디터와 이미지(최대 20장) 업로드를 지원합니다.</p>
            </div>
          </div>

          {error ? (
            <div className="mb-4 flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-destructive">{error}</p>
            </div>
          ) : null}

          {isLoading ? (
            <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 border border-border space-y-6">
              <Input
                type="text"
                label="제목"
                placeholder="피드 제목을 입력하세요"
                value={title}
                onChange={(e) => setTitle(e?.target?.value)}
                maxLength={120}
              />

              <Checkbox
                label="게시"
                description="체크하면 피드 목록에 공개됩니다"
                checked={isPublished}
                onChange={(e) => setIsPublished(e?.target?.checked)}
              />

              <div>
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">본문</label>
                <div className="bg-background border border-border rounded-md overflow-hidden">
                  <ReactQuill theme="snow" value={contentHtml} onChange={setContentHtml} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm md:text-base font-heading font-semibold text-foreground">이미지 첨부 ({totalImageCount}/{MAX_IMAGES})</label>
                  <input type="file" multiple accept="image/*" onChange={onSelectFiles} className="text-sm" />
                </div>

                {(aliveExistingImages || []).length > 0 ? (
                  <div>
                    <div className="text-xs text-muted-foreground caption mb-2">기존 이미지</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {aliveExistingImages.map((img) => (
                        <div key={img.id} className="relative border border-border rounded-md overflow-hidden bg-muted">
                          <img src={img.url} alt={img.originalName} className="w-full h-28 object-cover" />
                          <label className="absolute left-1 top-1 inline-flex items-center gap-1 rounded bg-background/90 px-2 py-1 text-[11px] text-foreground">
                            <input
                              type="radio"
                              name="feed-thumbnail"
                              checked={thumbnailSelection === `path:${img.filePath}`}
                              onChange={() => setThumbnailSelection(`path:${img.filePath}`)}
                            />
                            대표
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleExistingImage(img.id)}
                            className="absolute top-1 right-1 px-2 py-1 text-[11px] bg-destructive text-destructive-foreground rounded"
                          >
                            삭제
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {newFiles.length > 0 ? (
                  <div>
                    <div className="text-xs text-muted-foreground caption mb-2">새 이미지</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {newFiles.map((item) => (
                        <div key={item.id} className="relative border border-border rounded-md overflow-hidden bg-muted">
                          <img src={URL.createObjectURL(item.file)} alt={item.file.name} className="w-full h-28 object-cover" />
                          <label className="absolute left-1 top-1 inline-flex items-center gap-1 rounded bg-background/90 px-2 py-1 text-[11px] text-foreground">
                            <input
                              type="radio"
                              name="feed-thumbnail"
                              checked={thumbnailSelection === `new:${item.id}`}
                              onChange={() => setThumbnailSelection(`new:${item.id}`)}
                            />
                            대표
                          </label>
                          <button
                            type="button"
                            onClick={() => removeNewFile(item.id)}
                            className="absolute top-1 right-1 px-2 py-1 text-[11px] bg-destructive text-destructive-foreground rounded"
                          >
                            취소
                          </button>
                          <div className="px-2 py-1 text-[11px] text-muted-foreground truncate">{item.file.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="text-xs text-muted-foreground caption">
                  대표 썸네일을 지정하면 피드 목록/랜딩 피드에서 우선 노출됩니다.
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => navigate(-1)} disabled={isSaving}>
                  취소
                </Button>
                <Button variant="default" iconName="Check" iconPosition="left" onClick={save} loading={isSaving}>
                  저장
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedEditor;
